import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'

import { Body1 } from '@ui/Typography'
import { Modal } from '@ui/components/Modal'

import type { CurrencyType } from '@model/WalletDto'

import { Box } from '@shared/components/Box'

import { GroupPnL } from '../components/TradePnL'
import type { PortfolioTrade } from '../hooks/useDxtradePortfolioTrades'
import type { UseFixTradingWebSocketResult } from '../hooks/useFixTradingWebSocket'
import { formatSymbol } from '../utils/formatters'

import styles from './CloseAllTradesModal.module.scss'

interface CloseAllTradesModalProps {
  trades: PortfolioTrade[]
  closeAllPositions: UseFixTradingWebSocketResult['closeAllPositions']
  accountCurrency?: CurrencyType
  accountCode: string
  assetSymbol?: string
  assetDescription?: string
  onSuccess?: () => void
}

export const CloseAllTradesModal: BaseModalType<CloseAllTradesModalProps> = (props) => {
  const {
    close,
    closeAllPositions,
    trades,
    accountCurrency,
    accountCode,
    assetSymbol,
    assetDescription,
    onSuccess,
  } = props
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleConfirm = () => {
    if (!trades.length) return
    setIsLoading(true)
    const positionIds = trades.map((trade) => trade.tradeId)
    closeAllPositions({
      accountCode,
      positionIds,
      onSuccess,
      onSettled: () => {
        if (isMountedRef.current) setIsLoading(false)
        close()
      },
      symbol: assetSymbol,
    })
  }
  const title = assetSymbol
    ? `${t('Portfolio.Close All Trades')} (${formatSymbol(assetSymbol, assetDescription)})`
    : t('Portfolio.Close All Trades')
  return (
    <Modal>
      <Modal.Header canClose>{title}</Modal.Header>
      <Modal.Body>
        <Box fullWidth>
          <Box mb='x24'>
            <Body1 color='text-med-em'>
              {t('Portfolio.Are you sure you want to close all open positions')}
            </Body1>
          </Box>
          <Box>
            <Body1 color='text-med-em'>
              {t('Portfolio.Your profit/loss after this will be')}{' '}
              <GroupPnL
                trades={trades}
                accountCurrency={accountCurrency}
                wrapperStyles={false}
                className={styles.numericValue}
              />
            </Body1>
          </Box>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Primary onClick={handleConfirm} loading={isLoading} disabled={!trades.length}>
          {t('Confirm')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
